import React, { useState, useEffect } from "react";
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    useToast,
    useDisclosure,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    ButtonGroup,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from "@chakra-ui/react";
import {
    AddIcon,
    EditIcon,
    DeleteIcon,
} from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
    fetch_coupons,
    delete_coupon,
    resetCouponState,
} from "../../redux/couponSlice";
import { get_all_customer_list } from "../../redux/customerSlice";
import store from "../../redux/store";
import GasCylinderLoader from "../../components/GasCylinderLoader";
import CreateUpdateCoupon from "./CreateUpdateCoupon";

const Coupon = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();
    const cancelRef = React.useRef();

    const { loading, success, error, message, coupons, summary, totalCount, product_list } = useSelector((state) => state.coupon);

    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFilter, setSelectedFilter] = useState("All");
    const [deleteId, setDeleteId] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [usedProductCodes, setUsedProductCodes] = useState(new Set());
    const { all_customer_list } = useSelector((state) => state.customers);

    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
    const [initialCustomerIds, setInitialCustomerIds] = useState([]);
    const [formData, setFormData] = useState({
        id: "",
        coupon_code: "",
        description: "",
        discount_amount: "",
        additional_discount: "",
        validity: "",
        status: true,
    });

    // Fetch coupons and products on mount
    useEffect(() => {
        dispatch(fetch_coupons({ page: currentPage, limit: 10 }));
    }, [dispatch, currentPage]);


    const getProductDetails = (couponProducts) => {
        if (!Array.isArray(couponProducts)) return [];

        return couponProducts.map((cp) => {
            const match = product_list?.find(
                (p) => p.product_code === cp.prod_code
            );
            return match
                ? { name: match.name, rsp: match.rsp }
                : { name: cp.product_name, rsp: "N/A" };
        });
    };

    useEffect(() => {
        dispatch(fetch_coupons({
          action: "product_list"
        }));
      }, [dispatch]);
    useEffect(() => {
        dispatch(fetch_coupons({ page: currentPage, limit: 10 }));
    }, [dispatch, currentPage]);


    // Extract used product codes from all coupons
    useEffect(() => {
        if (coupons && coupons.length > 0) {
            const usedCodes = new Set();
            coupons.forEach((coupon) => {
                if (coupon.prod_code) {
                    if (Array.isArray(coupon.prod_code)) {
                        coupon.prod_code.forEach(code => usedCodes.add(code));
                    } else if (typeof coupon.prod_code === 'string') {
                        usedCodes.add(coupon.prod_code);
                    }
                }
                if (coupon.product_codes) {
                    if (Array.isArray(coupon.product_codes)) {
                        coupon.product_codes.forEach(code => usedCodes.add(code));
                    }
                }
            });
            setUsedProductCodes(usedCodes);
        }
    }, [coupons]);

    // Update available products when product_list changes or usedProductCodes changes
    useEffect(() => {
        if (product_list && product_list.length > 0) {
            const products = product_list
                .filter((p) => !usedProductCodes.has(p.product_code))
                .map((p) => ({
                    product_code: p.product_code,
                    name: p.name,
                    type: p.type,
                    rsp: p.rsp,
                }));
            setAvailableProducts(products);
        }
    }, [product_list, usedProductCodes]);

    // Filter coupons based on status and validity
    const filteredCoupons = coupons.filter((coupon) => {
        if (selectedFilter === "All") return true;

        const now = new Date();
        const validity = new Date(coupon.validity);
        const isExpired = validity < now;

        if (selectedFilter === "Active") {
            return coupon.status === "1" && !isExpired;
        }
        if (selectedFilter === "Inactive") {
            return coupon.status === "0" && !isExpired;
        }
        if (selectedFilter === "Expired") {
            return isExpired;
        }

        return true;
    });

    const handleProductSelect = (e) => {
        const selectedCode = e.target.value;
        if (!selectedCode) return;

        const product = availableProducts.find((p) => p.product_code === selectedCode);
        console.log(product, "this is pr")
        if (product) {
            setSelectedProducts((prev) => [...prev, product]);
            setAvailableProducts((prev) =>
                prev.filter((p) => p.product_code !== selectedCode)
            );
        }
    };  
    // Handle product removal
    const handleRemoveProduct = (productCode) => {
        const product = selectedProducts.find((p) => p.product_code === productCode);
        if (product) {
            setSelectedProducts((prev) =>
                prev.filter((p) => p.product_code !== productCode)
            );
            if (!usedProductCodes.has(productCode)) {
                setAvailableProducts((prev) => [...prev, product]);
            }
        }
    };

    // Open modal for creating coupon
    const handleOpenCreate = () => {
        if (!all_customer_list || all_customer_list.length === 0) {
            store.dispatch(get_all_customer_list());
        }

        setIsEditMode(false);
        setFormData({
            id: "",
            coupon_code: "",
            description: "",
            discount_amount: "",
            additional_discount: "",
            validity: "",
            status: true,
        });
        setSelectedProducts([]);
        setSelectedCustomerIds([]);
        setInitialCustomerIds([]);

        if (product_list && product_list.length > 0) {
            const products = product_list
                .filter((p) => !usedProductCodes.has(p.product_code))
                .map((p) => ({
                    product_code: p.product_code,
                    name: p.name,
                    rsp: p.rsp,
                    type: p.type,
                }));
            setAvailableProducts(products);
        }
        onOpen();
    };

    // Open modal for editing coupon
    const handleOpenEdit = (coupon) => {
        if (!all_customer_list || all_customer_list.length === 0) {
            store.dispatch(get_all_customer_list());
        }

        setIsEditMode(true);
        const formattedValidity = coupon.validity
            ? coupon.validity.replace(" ", "T").substring(0, 16)
            : "";

        // Check if coupon is expired
        const now = new Date();
        const validity = new Date(coupon.validity);
        const isExpired = validity < now;

        // Determine actual status
        let actualStatus = false;
        if (!isExpired && coupon.status === "1") {
            actualStatus = true;
        }

        // Pre-fill previously selected customers
       let customerIds = [];

       if (coupon.customers && Array.isArray(coupon.customers)) {
           customerIds = coupon.customers.map((c) => c.customer_id);
       }
       
       setSelectedCustomerIds(customerIds);
       setInitialCustomerIds(customerIds);
       
        setFormData({
            id: coupon.id,
            coupon_code: coupon.coupon_code,
            description: coupon.description,
            discount_amount: coupon.discount_amount,
            additional_discount: coupon.additional_discount,
            validity: formattedValidity,
            status: actualStatus,
        });

        
        const couponProducts = [];
        const productCodes = coupon.prod_code || coupon.product_codes || [];
        const productsData = coupon.products || [];
        
        // First try to get from products array (most reliable)
        if (Array.isArray(productsData) && productsData.length > 0) {
            productsData.forEach(prod => {
                const product = product_list.find(p => p.product_code === prod.prod_code);
                if (product) {
                    couponProducts.push({
                        product_code: product.product_code,
                        name: product.name,
                        rsp: product.rsp,
                        type: product.type,
                    });
                } else {
                    // Fallback to data from the coupon itself
                    couponProducts.push({
                        product_code: prod.prod_code,
                        name: prod.product_name,
                        rsp: prod.rsp || "N/A",
                        type: prod.type || "N/A",
                    });
                }
            });
        } else if (Array.isArray(productCodes) && productCodes.length > 0) {
            // Fallback to product codes
            productCodes.forEach(code => {
                const product = product_list.find(p => p.product_code === code);
                if (product) {
                    couponProducts.push({
                        product_code: product.product_code,
                        name: product.name,
                        rsp: product.rsp,
                        type: product.type,
                    });
                }
            });
        }
        
        console.log("Coupon Products for Edit:", couponProducts); // Debug log
        setSelectedProducts(couponProducts);

        const editingCouponCodes = new Set(couponProducts.map(p => p.product_code));
        const otherUsedCodes = new Set([...usedProductCodes].filter(code => !editingCouponCodes.has(code)));

        const products = product_list
            .filter((p) => !otherUsedCodes.has(p.product_code) && !editingCouponCodes.has(p.product_code))
            .map((p) => ({
                product_code: p.product_code,
                name: p.name,
                rsp: p.rsp,
            }));
        setAvailableProducts(products);

        onOpen();
    };

    // Open delete confirmation dialog
    const handleOpenDelete = (id) => {
        setDeleteId(id);
        onDeleteOpen();
    };

    // Handle delete coupon
    const handleDelete = () => {
        if (deleteId) {
            dispatch(delete_coupon(deleteId));
            onDeleteClose();
        }
    };

    // Toasts and reset after submission   
    useEffect(() => {
        if (success) {
            toast({
                title: "Success!",
                description: message,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            onClose();
            setFormData({
                id: "",
                coupon_code: "",
                description: "",
                discount_amount: "",
                additional_discount: "",
                validity: "",
                status: true,
            });
            setSelectedProducts([]);
            setSelectedCustomerIds([]);
            setInitialCustomerIds([]);
            dispatch(resetCouponState());
            dispatch(fetch_coupons({
                page: currentPage,
                limit: 10
            }));
        } else if (error) {
            toast({
                title: "Error",
                description: error,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            dispatch(resetCouponState());
        }
    }, [success, error, message, toast, onClose, dispatch, currentPage]);

    return (
        <Box bg="gray.50" minH="100vh" p={1}>
            {/* Header */}
            <Box bg={"white"} color="white" p={5} borderRadius="md" boxShadow="md">
                <Heading size="md" color="black">
                    🎫 Coupon Code Management
                </Heading>
                <Text fontSize="sm" color="gray.700">
                    Create, manage, and track promotional coupon codes
                </Text>
            </Box>

            {/* Summary Cards */}
            {summary && (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mt={6}>
                    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                        <Stat>
                            <StatLabel>Total Coupons</StatLabel>
                            <StatNumber color="purple.600">
                                {summary["Total Coupons"] || 0}
                            </StatNumber>
                        </Stat>
                    </Box>
                    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                        <Stat>
                            <StatLabel>Active Coupons</StatLabel>
                            <StatNumber color="green.600">
                                {summary["Active Coupons"] || 0}
                            </StatNumber>
                        </Stat>
                    </Box>
                    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                        <Stat>
                            <StatLabel>Total Usage</StatLabel>
                            <StatNumber color="blue.600">
                                {summary["Total Usage"] || 0}
                            </StatNumber>
                        </Stat>
                    </Box>
                    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                        <Stat>
                            <StatLabel>Total Discount Given</StatLabel>
                            <StatNumber color="orange.600">
                                ₹{summary["Total Discount Given"] || 0}
                            </StatNumber>
                        </Stat>
                    </Box>
                </SimpleGrid>
            )}

            {/* Filter Tabs and Create Button */}
            <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={4}>
                <ButtonGroup spacing={2}>
                    <Button
                        size="sm"
                        colorScheme={selectedFilter === "All" ? "blue" : "gray"}
                        variant={selectedFilter === "All" ? "solid" : "outline"}
                        onClick={() => setSelectedFilter("All")}
                    >
                        All
                    </Button>
                    <Button
                        size="sm"
                        colorScheme={selectedFilter === "Active" ? "blue" : "gray"}
                        variant={selectedFilter === "Active" ? "solid" : "outline"}
                        onClick={() => setSelectedFilter("Active")}
                    >
                        Active
                    </Button>
                    <Button
                        size="sm"
                        colorScheme={selectedFilter === "Inactive" ? "blue" : "gray"}
                        variant={selectedFilter === "Inactive" ? "solid" : "outline"}
                        onClick={() => setSelectedFilter("Inactive")}
                    >
                        Inactive
                    </Button>
                    <Button
                        size="sm"
                        colorScheme={selectedFilter === "Expired" ? "blue" : "gray"}
                        variant={selectedFilter === "Expired" ? "solid" : "outline"}
                        onClick={() => setSelectedFilter("Expired")}
                    >
                        Expired
                    </Button>
                </ButtonGroup>
                <Button
                    leftIcon={<AddIcon />}
                    colorScheme="green"
                    size="sm"
                    onClick={handleOpenCreate}
                >
                    Create Coupon
                </Button>
            </Flex>

            {/* Table */}
            <Box
                bg="white"
                borderWidth="1px"
                borderRadius="xl"
                mt={4}
                overflowX="auto"
                p={4}
            >
                {loading && coupons.length === 0 ? (
                    <GasCylinderLoader
                        size="sm"
                        loadingText="Loading Coupons..."
                    />
                ) : filteredCoupons.length === 0 ? (
                    <Flex
                        direction="column"
                        justify="center"
                        align="center"
                        minH="200px"
                        gap={2}
                    >
                        <Text fontSize="3xl">🎫</Text>
                        <Text color="gray.500" fontWeight="medium">
                            No coupons found
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                            Create, manage, and track promotional coupon codes
                        </Text>
                    </Flex>
                ) : (
                    <Table variant="simple" size="sm">
                        <Thead bg="gray.100">
                            <Tr>
                                <Th>Code</Th>
                                <Th>Description</Th>
                                <Th>Type</Th>
                                <Th>Products</Th>
                                <Th>Discount (₹)</Th>
                                <Th>Additional Discount (₹)</Th>
                                <Th>Created At</Th>
                                <Th>Validity</Th>
                                <Th>Status</Th>
                                <Th textAlign="center">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredCoupons.map((coupon) => {
                                const products = getProductDetails(coupon.products || []);
                                const now = new Date();
                                const validity = new Date(coupon.validity);
                                const isExpired = validity < now;

                                let statusDisplay = "Inactive";
                                let statusColor = "red";

                                if (isExpired) {
                                    statusDisplay = "Expired";
                                    statusColor = "yellow";
                                } else if (coupon.status === "1") {
                                    statusDisplay = "Active";
                                    statusColor = "green";
                                }

                                return (
                                    <Tr key={coupon.id} _hover={{ bg: "gray.50" }}>
                                        {/* Coupon Code */}
                                        <Td color="purple.600" fontWeight="semibold">
                                            {coupon.coupon_code}
                                        </Td>

                                        {/* Description */}
                                        <Td maxW="200px">
                                            <Text noOfLines={2}>{coupon.description}</Text>
                                        </Td>

                                        {/* Product Type */}
                                        <Td>
                                            {coupon.products && coupon.products.length > 0 ? (
                                                coupon.products.map((p, i) => (
                                                    <div key={i}>{p.type}</div>
                                                ))
                                            ) : (
                                                <span style={{ color: "gray" }}>No products found</span>
                                            )}
                                        </Td>

                                        {/* Product Name */}
                                        <Td>
                                            {coupon.products && coupon.products.length > 0 ? (
                                                coupon.products.map((p, i) => (
                                                    <div key={i}>{p.product_name}</div>
                                                ))
                                            ) : (
                                                <span style={{ color: "gray" }}>No products found</span>
                                            )}
                                        </Td>

                                        {/* Discount Amount */}
                                        <Td fontWeight="semibold" color="green.600">
                                            ₹{coupon.discount_amount}
                                        </Td>
                                        <Td fontWeight="semibold" color="green.600">
                                            ₹{coupon.additional_discount}
                                        </Td>

                                        {/* Created At */}
                                        <Td>{new Date(coupon.created_at).toLocaleDateString()}</Td>

                                        {/* Validity */}
                                        <Td>{new Date(coupon.validity).toLocaleDateString()}</Td>

                                        {/* Status */}
                                        <Td>
                                            <Badge colorScheme={statusColor}>{statusDisplay}</Badge>
                                        </Td>

                                        {/* Actions */}
                                        <Td textAlign="center">
                                            <Flex justify="center" gap={2}>
                                                <Button
                                                    size="xs"
                                                    leftIcon={<EditIcon />}
                                                    colorScheme="blue"
                                                    onClick={() => handleOpenEdit(coupon)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    leftIcon={<DeleteIcon />}
                                                    colorScheme="red"
                                                    onClick={() => handleOpenDelete(coupon.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </Flex>
                                        </Td>
                                    </Tr>

                                );
                            })}
                        </Tbody>
                    </Table>
                )}
            </Box>

            {/* Pagination Info */}
            {totalCount > 0 && (
                <Flex justify="center" mt={4}>
                    <Text fontSize="sm" color="gray.600">
                        Showing {filteredCoupons.length} of {totalCount} coupons
                    </Text>
                </Flex>
            )}

            {/* Create/Edit Coupon Modal */}
            <CreateUpdateCoupon
                isOpen={isOpen}
                onClose={onClose}
                isEditMode={isEditMode}
                initialFormData={formData}
                initialSelectedProducts={selectedProducts}
                initialSelectedCustomerIds={selectedCustomerIds}
                availableProducts={availableProducts}
                usedProductCodes={usedProductCodes}
                product_list={product_list}
                onProductSelect={handleProductSelect}
                onProductRemove={handleRemoveProduct}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                selectedCustomerIds={selectedCustomerIds}
                setSelectedCustomerIds={setSelectedCustomerIds}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Coupon
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure? You can't undo this action afterwards.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleDelete}
                                ml={3}
                                isLoading={loading}
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default Coupon;